import React, { useState, useCallback, memo, useEffect } from 'react';
import { Select, InputNumber, AutoComplete, Input } from 'antd';

const { Option } = Select;

// Editable Cell Component
const EditableCell = memo(({ 
    value, 
    record, 
    column, 
    onSave, 
    options = [], 
    placeholder = "Select...",
    loading = false,
    inputType = "select", // "select", "number", "text", "autocomplete"
    prefix = "", // New prop for prefix (like $)
    autoCompleteOptions = [], // For autocomplete functionality
    displayComponent = null // Custom display component function
}) => {
    const [editing, setEditing] = useState(false);
    const [currentValue, setCurrentValue] = useState(value);

    // Sync currentValue with value prop when it changes (e.g., after API reload)
    useEffect(() => {
        setCurrentValue(value);
    }, [value]);

    const handleSave = useCallback(async (newValue) => {
        if (newValue !== value) {
            const success = await onSave(record, column, newValue);
            if (success) {
                setCurrentValue(newValue);
            }
        }
        setEditing(false);
    }, [value, record, column, onSave]);

    const handleCancel = useCallback(() => {
        setCurrentValue(value);
        setEditing(false);
    }, [value]);

    if (editing) {
        // Render different input types based on inputType prop
        if (inputType === "number") {
            return (
                <InputNumber
                    value={currentValue}
                    onChange={setCurrentValue}
                    onBlur={() => handleSave(currentValue)}
                    onPressEnter={() => handleSave(currentValue)}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            handleCancel();
                        }
                    }}
                    style={{ width: '100%' }}
                    placeholder={placeholder}
                    precision={2}
                    min={0}
                    step={0.01}
                    autoFocus
                    prefix={prefix}
                />
            );
        }

        if (inputType === "autocomplete") {
            return (
                <AutoComplete
                    value={currentValue}
                    onChange={setCurrentValue}
                    onBlur={() => handleSave(currentValue)}
                    onPressEnter={() => handleSave(currentValue)}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            handleCancel();
                        }
                    }}
                    style={{ width: '100%' }}
                    placeholder={placeholder}
                    options={autoCompleteOptions}
                    filterOption={(inputValue, option) =>
                        option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                    }
                    autoFocus
                />
            );
        }

        if (inputType === "text") {
            return (
                <Input
                    value={currentValue || ''}
                    onChange={(e) => setCurrentValue(e.target.value)}
                    onBlur={() => handleSave(currentValue)}
                    onPressEnter={() => handleSave(currentValue)}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            handleCancel();
                        }
                    }}
                    style={{ width: '100%' }}
                    placeholder={placeholder}
                    autoFocus
                />
            );
        }
        
        // Default select input with search functionality
        return (
            <Select
                value={currentValue}
                onChange={handleSave}
                onBlur={handleCancel}
                onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                        handleCancel();
                    }
                }}
                style={{ width: '100%' }}
                placeholder={placeholder}
                loading={loading}
                autoFocus
                open
                showSearch
                filterOption={(input, option) => {
                    // Filter by both label and value
                    const label = option.children?.toString().toLowerCase() || '';
                    const value = option.value?.toString().toLowerCase() || '';
                    const searchInput = input.toLowerCase();
                    
                    return label.includes(searchInput) || value.includes(searchInput);
                }}
                optionFilterProp="children"
            >
                <Option value={null}>
                    <span style={{ color: '#999' }}>Not assigned</span>
                </Option>
                {options.map(option => (
                    <Option key={option.value} value={option.value}>
                        {option.label}
                    </Option>
                ))}
            </Select>
        );
    }

    // Display value when not editing
    if (inputType === "number") {
        return (
            <div
                onClick={() => setEditing(true)}
                className="editable-cell"
                style={{ textAlign: 'right', width: '100%' }} // Right align for number inputs
            >
                <span className="no-line-through" style={{ display: 'block', textAlign: 'right' }}>
                    {currentValue ? 
                        `${prefix}${parseFloat(currentValue).toFixed(2)}` : 
                        <span style={{ color: '#999', fontSize: 11, textAlign: 'right', display: 'block' }}>Click to set</span>
                    }
                </span>
            </div>
        );
    }

    if (inputType === "text" || inputType === "autocomplete") {
        return (
            <div
                onClick={() => setEditing(true)}
                className="editable-cell"
            >
                <span className="no-line-through">
                    {currentValue ? 
                        currentValue : 
                        <span style={{ color: '#999', fontSize: 11 }}>Click to enter</span>
                    }
                </span>
            </div>
        );
    }

    // Default display for select - check for custom display component
    return (
        <div
            onClick={() => setEditing(true)}
            className="editable-cell"
            style={{ width: '100%' }}
        >
            {displayComponent && currentValue !== null && currentValue !== undefined ? 
                displayComponent(currentValue) :
                <span className="no-line-through" style={{ width: '100%' }}>
                    {currentValue ? 
                        options.find(opt => opt.value === currentValue)?.label || 'Unknown' : 
                        <span style={{ color: '#999', fontSize: 11, textAlign: 'center' }}>Not assigned</span>
                    }
                </span>
            }
        </div>
    );
});

// Custom comparison function for memo to ensure proper re-rendering
const areEqual = (prevProps, nextProps) => {
    // Compare all props that can affect rendering except displayComponent
    // displayComponent is a function and comparing function references is unreliable
    const propsEqual = (
        prevProps.value === nextProps.value &&
        prevProps.loading === nextProps.loading &&
        prevProps.options === nextProps.options &&
        prevProps.autoCompleteOptions === nextProps.autoCompleteOptions &&
        prevProps.placeholder === nextProps.placeholder &&
        prevProps.inputType === nextProps.inputType &&
        prevProps.prefix === nextProps.prefix &&
        prevProps.record?.id === nextProps.record?.id
    );
    
    // For displayComponent, we rely on value changes to trigger re-renders
    // since displayComponent functions are recreated on each render
    return propsEqual;
};

export default React.memo(EditableCell, areEqual);